// frontend-react/src/components/RuleManagement/LegaltechRulesModal.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  DownloadOutlined,
  ExportOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { LegaltechRule, CsvUploadResult, RiskLevel } from '../../types/legaltechRule';
import {
  deleteLegaltechRule,
  downloadLegaltechCsvTemplate,
  exportLegaltechRulesCsv,
  listLegaltechTags,
  listLegaltechRules,
  normalizeBatch,
  uploadLegaltechRulesCsv,
} from '../../services/legaltechRuleService';
import LegaltechRuleEditModal from './LegaltechRuleEditModal';

const { Search } = Input;
const { Text } = Typography;

const RISK_LEVEL_LABELS: Record<RiskLevel, { text: string; color: string }> = {
  high: { text: '高', color: 'red' },
  medium: { text: '中', color: 'orange' },
  low: { text: '低', color: 'blue' },
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const LegaltechRulesModal: React.FC<Props> = ({ open, onClose }) => {
  const [rules, setRules] = useState<LegaltechRule[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // 篩選狀態
  const [filterTag, setFilterTag] = useState<string | undefined>(undefined);
  const [filterContractType, setFilterContractType] = useState<string | undefined>(undefined);
  const [filterRiskLevel, setFilterRiskLevel] = useState<string | undefined>(undefined);
  const [searchQ, setSearchQ] = useState('');

  // 編輯 Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<LegaltechRule | null>(null);

  // CSV 上傳 Modal
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvMode, setCsvMode] = useState<'append' | 'replace'>('append');
  const [csvUploading, setCsvUploading] = useState(false);
  const fileRef = useRef<File | null>(null);

  // 批次標準化
  const [batchNormalizing, setBatchNormalizing] = useState(false);

  // 分頁
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: {
        tag?: string;
        contract_type?: string;
        risk_level?: string;
        q?: string;
      } = {};
      if (filterTag) params.tag = filterTag;
      if (filterContractType) params.contract_type = filterContractType;
      if (filterRiskLevel) params.risk_level = filterRiskLevel;
      if (searchQ) params.q = searchQ;

      const [rulesData, tagsData] = await Promise.all([
        listLegaltechRules(params),
        listLegaltechTags(),
      ]);
      setRules(rulesData);
      setAllTags(tagsData);
    } catch (err: any) {
      message.error(err?.message ?? '載入規則失敗');
    } finally {
      setLoading(false);
    }
  }, [filterTag, filterContractType, filterRiskLevel, searchQ]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const handleDelete = async (id: number) => {
    try {
      await deleteLegaltechRule(id);
      message.success('規則已停用');
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      message.error(err?.message ?? '刪除失敗');
    }
  };

  const handleEditSaved = (saved: LegaltechRule) => {
    setRules(prev => {
      const idx = prev.findIndex(r => r.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    setEditModalOpen(false);
    setEditingRule(null);
    listLegaltechTags()
      .then(setAllTags)
      .catch(() => {});
  };

  const handleCsvUpload = async () => {
    if (!fileRef.current) {
      message.warning('請選擇 CSV 檔案');
      return;
    }
    setCsvUploading(true);
    try {
      const result: CsvUploadResult = await uploadLegaltechRulesCsv(fileRef.current, csvMode);
      const summary = `新增 ${result.created} 條，略過 ${result.skipped} 條`;
      if (result.errors.length > 0) {
        message.warning(`上傳完成（${summary}），有 ${result.errors.length} 列有問題`);
        console.warn('[律果規則] CSV 上傳警告:', result.errors);
      } else {
        message.success(`上傳完成：${summary}`);
      }
      setCsvModalOpen(false);
      fileRef.current = null;
      loadData();
    } catch (err: any) {
      message.error(err?.message ?? 'CSV 上傳失敗');
    } finally {
      setCsvUploading(false);
    }
  };

  const handleBatchNormalize = async () => {
    setBatchNormalizing(true);
    try {
      const result = await normalizeBatch();
      message.success(
        `批次 AI 標準化完成：處理 ${result.processed} 條，成功 ${result.success} 條，失敗 ${result.failed} 條`
      );
      loadData();
    } catch (err: any) {
      message.error(err?.message ?? '批次標準化失敗');
    } finally {
      setBatchNormalizing(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      await exportLegaltechRulesCsv();
      message.success('CSV 匯出成功');
    } catch (err: any) {
      message.error(err?.message ?? 'CSV 匯出失敗');
    }
  };

  const openEdit = (record: LegaltechRule) => {
    setEditingRule(record);
    setEditModalOpen(true);
  };

  const columns: ColumnsType<LegaltechRule> = [
    {
      title: '編號',
      key: 'index',
      width: 46,
      align: 'center',
      render: (_: any, __: LegaltechRule, index: number) => (
        <Text type='secondary' style={{ fontSize: 11 }}>
          {(currentPage - 1) * pageSize + index + 1}
        </Text>
      ),
    },
    {
      title: '規則名稱',
      dataIndex: 'name',
      width: 192,
      render: (name: string) => {
        const truncated = name.length > 20 ? name.slice(0, 20) + '…' : name;
        return (
          <Tooltip title={name.length > 20 ? name : undefined}>
            <Text style={{ fontSize: 12 }}>{truncated}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: '規則內容（預覽）',
      dataIndex: 'content',
      ellipsis: true,
      render: (content: string) => (
        <Text type='secondary' style={{ fontSize: 11 }}>
          {content.slice(0, 60)}
          {content.length > 60 ? '...' : ''}
        </Text>
      ),
    },
    {
      title: '類型',
      dataIndex: 'contract_type',
      width: 64,
      align: 'center',
      render: (ct?: string) => {
        if (!ct) return <Text type='secondary'>—</Text>;
        const short = ct.length > 3 ? ct.slice(0, 3) : ct;
        return (
          <Tooltip title={ct.length > 3 ? ct : undefined}>
            <Tag style={{ fontSize: 11, margin: 0 }}>{short}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '標籤',
      key: 'tags',
      width: 160,
      render: (_: any, record: LegaltechRule) => {
        // 優先顯示 issue_tags，fallback 到 category
        const tags = record.issue_tags?.length ? record.issue_tags : (record.category ? [record.category] : []);
        if (!tags.length) return <Text type='secondary'>—</Text>;
        return (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {tags.map(t => (
              <Tag key={t} color='blue' style={{ fontSize: 11, margin: 0 }}>{t}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: '風險',
      dataIndex: 'risk_level',
      width: 46,
      align: 'center',
      render: (level?: RiskLevel) => {
        if (!level) return <Text type='secondary'>—</Text>;
        const label = RISK_LEVEL_LABELS[level];
        if (!label) return <Text type='secondary'>—</Text>;
        const colorMap: Record<string, { color: string; bg: string; border: string }> = {
          red: { color: '#d4380d', bg: '#fff2e8', border: '#ffbb96' },
          orange: { color: '#d46b08', bg: '#fff7e6', border: '#ffd591' },
          blue: { color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
        };
        const c = colorMap[label.color] ?? colorMap['blue']!;
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            fontSize: 11,
            fontWeight: 500,
            color: c.color,
            background: c.bg,
            border: `1px solid ${c.border}`,
          }}>
            {label.text}
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 56,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title='確定停用此規則？'
          description='停用後將不再出現在審閱篩選中。'
          onConfirm={e => {
            e?.stopPropagation();
            handleDelete(record.id);
          }}
          onCancel={e => e?.stopPropagation()}
          okText='停用'
          cancelText='取消'
          okButtonProps={{ danger: true }}
        >
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            onClick={e => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <span>律果規則庫</span>
            <Tag color='red'>Super Admin</Tag>
          </Space>
        }
        open={open}
        onCancel={onClose}
        footer={null}
        width={1200}
        styles={{
          body: { padding: '12px 24px 24px', height: 'calc(100vh - 120px)', overflow: 'auto' },
        }}
        style={{ top: 20 }}
      >
        {/* 工具列 */}
        <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Search
              placeholder='搜尋規則名稱...'
              allowClear
              style={{ width: 180 }}
              onSearch={v => setSearchQ(v)}
              onChange={e => !e.target.value && setSearchQ('')}
            />
            <Select
              allowClear
              placeholder='適用標籤'
              style={{ width: 180 }}
              showSearch
              options={allTags.map(t => ({ label: t, value: t }))}
              value={filterTag}
              onChange={setFilterTag}
            />
            <Select
              allowClear
              placeholder='合約類型'
              style={{ width: 130 }}
              options={[
                { label: '定型化契約', value: '定型化契約' },
                { label: '一般合約', value: '一般合約' },
              ]}
              value={filterContractType}
              onChange={setFilterContractType}
            />
            <Select
              allowClear
              placeholder='風險等級'
              style={{ width: 110 }}
              options={[
                { label: '高', value: 'high' },
                { label: '中', value: 'medium' },
                { label: '低', value: 'low' },
              ]}
              value={filterRiskLevel}
              onChange={setFilterRiskLevel}
            />
          </Space>
          <Space wrap>
            <Button
              icon={<ThunderboltOutlined />}
              loading={batchNormalizing}
              onClick={handleBatchNormalize}
            >
              批次 AI 標準化
            </Button>
            <Button icon={<ExportOutlined />} onClick={handleExportCsv}>
              匯出 CSV
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadLegaltechCsvTemplate}>
              CSV 範本
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setCsvModalOpen(true)}>
              上傳 CSV
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRule(null);
                setEditModalOpen(true);
              }}
            >
              新增規則
            </Button>
          </Space>
        </Space>

        {/* 規則表格：點任一列開啟編輯 */}
        <Table
          columns={columns}
          dataSource={rules}
          rowKey='id'
          loading={loading}
          size='small'
          scroll={{ y: 'calc(100vh - 300px)' }}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: false,
            showTotal: t => `共 ${t} 條`,
            onChange: page => setCurrentPage(page),
          }}
          onRow={record => ({
            onClick: () => openEdit(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>

      {/* 新增/編輯 Modal */}
      <LegaltechRuleEditModal
        open={editModalOpen}
        rule={editingRule}
        availableTags={allTags}
        onClose={() => {
          setEditModalOpen(false);
          setEditingRule(null);
        }}
        onSaved={handleEditSaved}
      />

      {/* CSV 上傳 Modal */}
      <Modal
        title='批量上傳律果規則 CSV'
        open={csvModalOpen}
        onOk={handleCsvUpload}
        onCancel={() => {
          setCsvModalOpen(false);
          fileRef.current = null;
        }}
        okText='開始上傳'
        cancelText='取消'
        confirmLoading={csvUploading}
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <Select
            value={csvMode}
            onChange={setCsvMode}
            style={{ width: '100%' }}
            options={[
              { label: 'Append（新增，不刪除現有規則）', value: 'append' },
              { label: 'Replace（全部替換，先停用舊規則）', value: 'replace' },
            ]}
          />
          <Upload
            accept='.csv'
            maxCount={1}
            beforeUpload={file => {
              fileRef.current = file;
              return false;
            }}
            onRemove={() => {
              fileRef.current = null;
            }}
          >
            <Button icon={<UploadOutlined />}>選擇 CSV 檔案</Button>
          </Upload>
          <Text type='secondary' style={{ fontSize: 12 }}>
            CSV 必要欄位：規則名稱、規則內容。選填：適用標籤（逗號分隔）、合約類型、選取模式、觸發關鍵字、觸發條件、通過標準、不通過標準、例外情況、建議輸出、風險等級、範例、排序。
          </Text>
        </Space>
      </Modal>
    </>
  );
};

export default LegaltechRulesModal;
